import { Controller } from "@hotwired/stimulus";
import { effect } from "@preact/signals-core";
import { edgeAt, edgePaintable, edgeSegment, edgesOf, hasMenu, type Mark, markOf, playersCode, type Switch, setEdges, withSwitch } from "../maps/edges";
import { geometryWrite, sceneTerrain } from "../maps/geometry";
import type MapBoard from "./map_board_controller";

const GAP = 12;
// Half the mark's highlight, as a share of the shorter cell side.
const HIGHLIGHT = 0.15;
// Tailwind's max-sm: below it the menu is a bottom sheet, placed by CSS.
const SHEET = "(max-width: 639px)";

// Connects to data-controller="map-edge-menu", beside map-board on the
// master's own board root. The board opens this small menu on a tap near a
// door, window or arrow slit with nothing armed; its title, note and switches
// follow the mark's code, and each switch is one write of the whole edges
// through the board, which leaves the menu open. While it is open the mark is
// highlighted as an edge gesture's edges are.
export default class extends Controller {
	static targets = ["menu", "title", "note", "switch"];
	static values = { titles: Object, notes: Object };
	declare readonly menuTarget: HTMLElement;
	declare readonly titleTarget: HTMLElement;
	declare readonly noteTarget: HTMLElement;
	declare readonly switchTargets: HTMLButtonElement[];
	declare readonly titlesValue: Record<string, string>;
	declare readonly notesValue: Record<string, string>;
	edge?: Mark | null;
	dispose?: (() => void) | null;
	opening?: number;

	disconnect() {
		this.close();
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard | null;
	}

	open(edge: Mark) {
		const { state } = this.board;
		this.close();
		this.edge = edge;
		this.opening = (this.opening ?? 0) + 1;
		const { opening } = this;
		this.menuTarget.hidden = false;
		state.local.drafts.value = { ...state.local.drafts.peek(), edgePick: [edge] };
		this.dispose = effect(() => {
			state.view.value.scene;
			state.local.drafts.value.grid;
			state.local.viewport.value;
			const code = this.markCode(edge);
			// A resync may take the mark away and bring it back in one turn.
			if (!code) return queueMicrotask(() => this.opening === opening && !this.markCode(edge) && this.close());
			this.show(code);
			this.place();
		});
		[...this.menuTarget.querySelectorAll("button")].find((button) => !button.closest("[hidden]"))?.focus({ preventScroll: true });
	}

	// The mark's code while the board draws it, on a grid being calibrated too.
	markCode(edge: Mark) {
		const board = this.board;
		const scene = board?.state.view.peek().scene;
		if (!scene || !edgePaintable(board.gridMath(), scene, edge)) return null;
		const code = edgeAt(edgesOf(scene.edges), edge);
		return hasMenu(code) ? code : null;
	}

	// A window and a slit have no switch but Secreta, and a false door no lock.
	show(code: string) {
		const mark = markOf(code);
		this.titleTarget.textContent = this.titlesValue[code];
		this.noteTarget.textContent = this.notesValue[playersCode(code)];
		for (const button of this.switchTargets) {
			const name = button.dataset.switch as Switch;
			button.hidden = mark?.kind !== "door" && name !== "secret";
			button.setAttribute("aria-pressed", String(Boolean(mark?.[name])));
			button.disabled = name === "locked" && Boolean(mark?.fake);
		}
	}

	close() {
		if (!this.edge) return;
		this.dispose?.();
		this.dispose = null;
		this.edge = null;
		this.menuTarget.hidden = true;
		const drafts = this.board?.state.local.drafts;
		if (drafts?.peek().edgePick) {
			const { edgePick: _picked, ...rest } = drafts.peek();
			drafts.value = rest;
		}
		if (this.menuTarget.contains(document.activeElement)) this.board?.canvasTarget.focus({ preventScroll: true });
	}

	dismiss() {
		this.close();
	}

	toggle({ currentTarget: button }: Event & { currentTarget: HTMLButtonElement }) {
		const code = this.edge && this.markCode(this.edge);
		const next = code && withSwitch(code, button.dataset.switch as Switch, button.getAttribute("aria-pressed") !== "true");
		if (next) this.write(next);
		button.focus({ preventScroll: true });
	}

	remove() {
		this.write(".");
		this.close();
	}

	write(code: string) {
		const { edge, board } = this;
		if (!edge || !board) return;
		const { scene } = board.state.view.peek();
		const write = geometryWrite(scene, sceneTerrain(scene.terrain), setEdges(edgesOf(scene.edges), [edge], code));
		if (write) board.updateScene(write.attrs, write.local);
	}

	// Beside the mark, past its end on a grid row, flipped to its left or
	// clamped when the board runs out, as the token menu is placed.
	place() {
		const { state, renderer } = this.board;
		if (!renderer) return;
		if (window.matchMedia(SHEET).matches) {
			this.menuTarget.style.maxHeight = "";
			return;
		}
		// Below the top bar, so Desfazer stays in reach after a switch, and no taller than the room under it.
		const board = (this.menuTarget.offsetParent ?? this.element).getBoundingClientRect();
		const floor = this.board.chromeInsets().top + GAP;
		this.menuTarget.style.maxHeight = `${Math.max(board.height - floor - GAP, 0)}px`;
		const math = this.board.gridMath();
		const { x1, y1, x2, y2 } = edgeSegment(math, this.edge);
		const at = renderer.imageToClient({ x: (x1 + x2) / 2, y: (y1 + y2) / 2 });
		const menu = this.menuTarget.getBoundingClientRect();
		const across = Math.abs(x2 - x1) / 2 + Math.min(math.grid.cell_w, math.grid.cell_h) * HIGHLIGHT;
		const reach = across * state.local.viewport.peek().zoom + GAP;
		let left = at.x - board.left + reach;
		if (left + menu.width > board.width - GAP) left = at.x - board.left - reach - menu.width;
		left = Math.min(Math.max(left, GAP), Math.max(board.width - menu.width - GAP, GAP));
		const top = Math.min(Math.max(at.y - board.top - menu.height / 2, floor), Math.max(board.height - menu.height - GAP, floor));
		this.menuTarget.style.left = `${left}px`;
		this.menuTarget.style.top = `${top}px`;
	}
}
